/**
 * @fileoverview Firebase AI Test Runner
 *
 * Runs integration tests through Firebase with AI-generated moves.
 * This is the KEY test runner for reproducing production AI+Firebase bugs:
 * - Piece ID regeneration after FEN decode
 * - Stale piece references across Firebase round-trips
 * - Status effect preservation through sync
 * - Engine consistency after AI move + Firebase sync
 *
 * Replicates the full BotGameController pipeline within the
 * DualEngineCoordinator testing framework.
 */

import { AIOrchestrator } from "../../zodiacEngine/AI/Core/AIOrchestrator";
import { AIPlayerType } from "../../zodiacEngine/AI/Core/IAIPlayer";
import {
  AIModel,
  TimeFrame,
  getBaseAITypeFromModel,
  getTimeLimitFromFrame,
  AI_MODEL_CONFIG,
} from "../../zodiacEngine/AI/Core/AIVariants";
import { MinimaxAI } from "../../zodiacEngine/AI/MinimaxAI";
import { ZodiacFEN } from "../../zodiacEngine/Fen/ZodiacFEN";
import { State } from "../../zodiacEngine/types";
import { IntegrationTest } from "../types";
import { TestStep } from "../LocalTestRunner";
import { AITestConfig, AIFirebaseTestResult, AIValidationCheck } from "../AITestTypes";
import { DualEngineCoordinator } from "./DualEngineCoordinator";

/**
 * Run a single AI test through Firebase P2P
 */
export async function runAIFirebaseTest(
  test: IntegrationTest,
  config: AITestConfig
): Promise<AIFirebaseTestResult> {
  const startTime = performance.now();
  const coordinator = new DualEngineCoordinator();

  const steps: TestStep[] = [
    { name: "Initialize Dual Engines", status: "pending" },
    { name: "Setup Firebase Connection", status: "pending" },
    { name: "Configure AI", status: "pending" },
    { name: "Request AI Move", status: "pending" },
    { name: "Execute & Sync via Firebase", status: "pending" },
    { name: "Verify Engine Consistency", status: "pending" },
    { name: "Validate Result", status: "pending" },
  ];

  const updateStep = (
    index: number,
    status: TestStep["status"],
    message?: string
  ) => {
    steps[index].status = status;
    steps[index].timestamp = performance.now();
    if (message) steps[index].message = message;
  };

  const createResult = (
    passed: boolean,
    extras?: Partial<AIFirebaseTestResult>
  ): AIFirebaseTestResult => ({
    _type: "ai-firebase",
    test,
    passed,
    duration: performance.now() - startTime,
    aiMoveMade: "",
    aiThinkingTimeMs: 0,
    aiConfidence: 0,
    beforeFEN: "",
    afterFEN: "",
    validationChecks: [],
    steps,
    firebaseRoundTrips: coordinator.getRoundTripCount(),
    pieceIdChanges: coordinator.getPieceIdChanges(),
    stateConsistencyChecks: coordinator.getConsistencyChecks(),
    syncTimings: coordinator.getSyncTimings(),
    lightEngineFEN: "",
    darkEngineFEN: "",
    enginesConsistent: false,
    ...extras,
  });

  try {
    // Step 1: Initialize dual engines
    updateStep(0, "running");
    await DualEngineCoordinator.ensureTestPlayersArePlaced();
    const gameId = await coordinator.initialize({
      testId: test.name.replace(/\s+/g, "_"),
      inputFEN: test.inputFEN,
      lightKing: test.lightKing,
      darkKing: test.darkKing,
      lightPlayerId: "test-light-player",
      darkPlayerId: "test-dark-player",
    });
    updateStep(
      0,
      "passed",
      `Engines initialized with gameId: ${gameId.slice(0, 16)}...`
    );

    // Step 2: Firebase connection
    updateStep(1, "passed", "Firebase listeners active on both engines");

    // Step 3: Configure AI on the moving engine
    updateStep(2, "running");
    const movingEngine =
      config.side === "light"
        ? coordinator.getLightEngine()
        : coordinator.getDarkEngine();
    const aiOrchestrator = new AIOrchestrator(movingEngine);
    const aiType = getBaseAITypeFromModel(config.aiModel);

    if (config.side === "light") {
      aiOrchestrator.setLightPlayer(aiType);
      aiOrchestrator.setDarkPlayer(AIPlayerType.PLAYER);
    } else {
      aiOrchestrator.setDarkPlayer(aiType);
      aiOrchestrator.setLightPlayer(AIPlayerType.PLAYER);
    }

    // Configure minimax model if needed
    const modelConfig = AI_MODEL_CONFIG[config.aiModel];
    if (modelConfig.minimaxModel) {
      const minimaxAI = aiOrchestrator.getAI(
        AIPlayerType.MINIMAX
      ) as MinimaxAI;
      if (minimaxAI) {
        minimaxAI.setModel(modelConfig.minimaxModel);
      }
    }

    updateStep(
      2,
      "passed",
      `AI: ${modelConfig.displayName} (${aiType}) as ${config.side}`
    );

    // Step 4: Request AI move
    updateStep(3, "running");
    const beforeFEN =
      config.side === "light"
        ? coordinator.getLightEngineFEN()
        : coordinator.getDarkEngineFEN();
    const beforeState = movingEngine.getGameState();
    const beforePieceCount = countPieces(beforeState.board);

    // Ensure correct turn
    const gameState = (movingEngine as any).gameStateManager.getGameState();
    gameState.isLightTurn = config.side === "light";

    const timeLimit = getTimeLimitFromFrame(config.timeFrame);
    const currentState = movingEngine.getGameState();
    const aiThinkStart = performance.now();
    const moveResult = await aiOrchestrator.requestAIMove(
      currentState,
      timeLimit
    );
    const aiThinkingTimeMs = performance.now() - aiThinkStart;

    if (!moveResult) {
      updateStep(3, "failed", "AI returned no move");
      for (let i = 4; i < steps.length; i++) updateStep(i, "skipped");
      await coordinator.cleanup();
      return createResult(false, {
        error: "AI returned no move",
        beforeFEN,
        validationChecks: [
          {
            name: "ai_returned_move",
            passed: false,
            details: "AI returned null",
          },
        ],
      });
    }

    updateStep(
      3,
      "passed",
      `AI chose move in ${Math.round(aiThinkingTimeMs)}ms (confidence: ${moveResult.confidence.toFixed(2)})`
    );

    // Step 5: Execute move and sync via Firebase (the critical path!)
    updateStep(4, "running");
    const execResult = await coordinator.executeAIMoveAndSync(
      config.side,
      moveResult,
      currentState
    );

    if (!execResult.success) {
      updateStep(4, "failed", execResult.error);
      for (let i = 5; i < steps.length; i++) updateStep(i, "skipped");
      await coordinator.cleanup();
      return createResult(false, {
        error: execResult.error,
        beforeFEN,
        aiThinkingTimeMs,
        aiConfidence: moveResult.confidence,
        aiReasoning: moveResult.reasoning,
        validationChecks: [
          { name: "ai_returned_move", passed: true },
          {
            name: "move_executed",
            passed: false,
            details: execResult.error,
          },
        ],
      });
    }

    updateStep(
      4,
      "passed",
      `Move executed and synced via Firebase (${coordinator.getRoundTripCount()} round-trip(s))`
    );

    // Step 6: Verify engine consistency
    updateStep(5, "running");
    await coordinator.waitForSyncComplete();
    const consistencyChecks = coordinator.verifyConsistency();
    const enginesConsistent = consistencyChecks.every((c) => c.matches);

    if (!enginesConsistent) {
      const mismatches = consistencyChecks.filter((c) => !c.matches);
      updateStep(
        5,
        "failed",
        `Consistency mismatch: ${mismatches.map((m) => m.field).join(", ")}`
      );
    } else {
      updateStep(5, "passed", "Both engines agree on state");
    }

    // Step 7: Validate result
    updateStep(6, "running");
    const lightFEN = coordinator.getLightEngineFEN();
    const darkFEN = coordinator.getDarkEngineFEN();
    const afterState = movingEngine.getGameState();
    const afterFEN = ZodiacFEN.encode(afterState);

    // Build move description
    const movingPiece = moveResult.movingPiece;
    const aiMoveMade = movingPiece
      ? `${config.side === "light" ? "Light" : "Dark"} ${movingPiece.type} (${moveResult.fromPosition.row},${moveResult.fromPosition.col}) -> (${moveResult.move.position.row},${moveResult.move.position.col})`
      : `(${moveResult.fromPosition.row},${moveResult.fromPosition.col}) -> (${moveResult.move.position.row},${moveResult.move.position.col})`;

    // Run validation checks (local + Firebase-specific)
    const validationChecks = runFirebaseValidationChecks(
      beforeState,
      afterState,
      beforeFEN,
      afterFEN,
      beforePieceCount,
      config.side,
      timeLimit,
      aiThinkingTimeMs,
      enginesConsistent,
      coordinator.getPieceIdChanges(),
      lightFEN,
      darkFEN
    );

    const allPassed = validationChecks.every((c) => c.passed);
    const failedChecks = validationChecks.filter((c) => !c.passed);

    if (allPassed) {
      updateStep(6, "passed", `All ${validationChecks.length} checks passed`);
    } else {
      updateStep(
        6,
        "failed",
        `${failedChecks.length} check(s) failed: ${failedChecks.map((c) => c.name).join(", ")}`
      );
    }

    const bonusTurnSync = coordinator.getBonusTurnSync();

    await coordinator.cleanup();

    return createResult(allPassed, {
      error: allPassed
        ? undefined
        : `Validation failed: ${failedChecks.map((c) => `${c.name}: ${c.details}`).join("; ")}`,
      beforeFEN,
      afterFEN,
      aiMoveMade,
      aiThinkingTimeMs,
      aiConfidence: moveResult.confidence,
      aiReasoning: moveResult.reasoning,
      validationChecks,
      lightEngineFEN: lightFEN,
      darkEngineFEN: darkFEN,
      enginesConsistent,
      bonusTurnSync,
      stateConsistencyChecks: consistencyChecks,
    });
  } catch (error) {
    steps.forEach((step, i) => {
      if (step.status === "pending" || step.status === "running") {
        updateStep(
          i,
          "failed",
          error instanceof Error ? error.message : String(error)
        );
      }
    });

    await coordinator.cleanup();

    return createResult(false, {
      error: error instanceof Error ? error.message : String(error),
      validationChecks: [
        {
          name: "no_crash",
          passed: false,
          details: error instanceof Error ? error.message : String(error),
        },
      ],
    });
  }
}

/**
 * Run validation checks including Firebase-specific ones
 */
function runFirebaseValidationChecks(
  beforeState: any,
  afterState: any,
  beforeFEN: string,
  afterFEN: string,
  beforePieceCount: number,
  side: "light" | "dark",
  timeLimit: number,
  thinkingTime: number,
  enginesConsistent: boolean,
  pieceIdChanges: any[],
  lightFEN: string,
  darkFEN: string
): AIValidationCheck[] {
  const checks: AIValidationCheck[] = [];

  // Standard validation checks
  checks.push({ name: "ai_returned_move", passed: true });
  checks.push({ name: "move_executed", passed: true });

  // Board integrity
  const boardRows = afterState.board?.length ?? 0;
  const boardCols = afterState.board?.[0]?.length ?? 0;
  checks.push({
    name: "board_integrity",
    passed: boardRows === 8 && boardCols === 5,
    details: `${boardRows}x${boardCols}`,
  });

  // Piece count
  const afterPieceCount = countPieces(afterState.board);
  checks.push({
    name: "piece_count_valid",
    passed: afterPieceCount <= beforePieceCount,
    details: `Before: ${beforePieceCount}, After: ${afterPieceCount}`,
  });

  // Turn changed
  const gameEnded =
    afterState.state === State.PostGame ||
    afterState.state === State.Draw ||
    afterState.state === State.Terminated ||
    afterState.lightWon !== undefined;
  const isBonusTurn =
    afterState.skipTurnChange || afterState.activeBonusTurnPieceId;
  const turnChanged = beforeState.isLightTurn !== afterState.isLightTurn;

  if (gameEnded || isBonusTurn) {
    checks.push({
      name: "turn_changed",
      passed: true,
      details: gameEnded ? "Game ended" : "Bonus turn active",
    });
  } else {
    checks.push({
      name: "turn_changed",
      passed: turnChanged,
      details: turnChanged ? `Turn changed from ${side}` : `Turn did NOT change`,
    });
  }

  // State valid
  const validStates = [State.Playing, State.PostGame, State.Draw, State.Terminated];
  checks.push({
    name: "state_valid",
    passed: validStates.includes(afterState.state),
    details: `State: ${afterState.state}`,
  });

  // AI timing
  const timingBuffer = 5000;
  checks.push({
    name: "ai_timing",
    passed: thinkingTime <= timeLimit + timingBuffer,
    details: `${Math.round(thinkingTime)}ms (limit: ${timeLimit}ms)`,
  });

  // === Firebase-specific checks ===

  // Engines consistent
  checks.push({
    name: "engines_consistent",
    passed: enginesConsistent,
    details: enginesConsistent
      ? "Both engines match"
      : `Light FEN: ${lightFEN}, Dark FEN: ${darkFEN}`,
  });

  // Piece IDs resolved
  const failedResolutions = pieceIdChanges.filter(
    (c) => c.resolvedBy === "failed"
  );
  checks.push({
    name: "piece_ids_resolved",
    passed: failedResolutions.length === 0,
    details:
      failedResolutions.length === 0
        ? `${pieceIdChanges.length} ID change(s) resolved`
        : `${failedResolutions.length} piece(s) failed to resolve`,
  });

  // Status preserved (compare status symbol counts in light and dark FENs)
  const lightStatusCount = countStatusSymbols(lightFEN);
  const darkStatusCount = countStatusSymbols(darkFEN);
  checks.push({
    name: "status_preserved",
    passed: lightStatusCount === darkStatusCount,
    details:
      lightStatusCount === darkStatusCount
        ? `${lightStatusCount} status effect(s) consistent`
        : `Light: ${lightStatusCount}, Dark: ${darkStatusCount} (mismatch)`,
  });

  return checks;
}

/**
 * Count pieces on board
 */
function countPieces(board: any[][]): number {
  let count = 0;
  for (const row of board) {
    for (const square of row) {
      count += square.Pieces?.length || 0;
    }
  }
  return count;
}

/**
 * Count status symbols in FEN
 */
function countStatusSymbols(fen: string): number {
  const statusRegex = /[!☣⛓z☠#$*✳️⚘]/g;
  const matches = fen.match(statusRegex);
  let count = matches ? matches.length : 0;
  const virusInfectionRegex = /\+[vV]/g;
  const virusMatches = fen.match(virusInfectionRegex);
  count += virusMatches ? virusMatches.length : 0;
  return count;
}

/**
 * Run an entire test suite with AI through Firebase
 */
export async function runAIFirebaseTestSuite(
  suiteName: string,
  tests: IntegrationTest[],
  config: AITestConfig
): Promise<{
  suiteName: string;
  results: AIFirebaseTestResult[];
  passed: number;
  failed: number;
  duration: number;
}> {
  const startTime = performance.now();
  const results: AIFirebaseTestResult[] = [];

  for (const test of tests) {
    const result = await runAIFirebaseTest(test, config);
    results.push(result);
    // Small delay between tests to avoid Firebase rate limiting
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return {
    suiteName,
    results,
    passed: results.filter((r) => r.passed).length,
    failed: results.filter((r) => !r.passed).length,
    duration: performance.now() - startTime,
  };
}
